import React, { useState, useEffect } from 'react';
import { ensureAuth, auth } from './firebase';
import { getSavedNickname } from './shared/fiveLeague';
import { initQuestionsFromFirestore } from './constants/questions';

// Components
import ErrorBoundary from './components/ErrorBoundary';

// Screens
import Home from './screens/Home';
import Wait from './screens/Wait';
import Game from './screens/Game';
import Result from './screens/Result';
import TVDisplay from './screens/TVDisplay';
import Admin from './screens/Admin';
import Privacy from './screens/Privacy';
import Terms from './screens/Terms';
import Legal from './screens/Legal';
// チェーンモード
import ChainOrder from './screens/ChainOrder';
import ChainGame from './screens/ChainGame';
import ChainTV from './screens/ChainTV';
import ChainResult from './screens/ChainResult';

// シンプルなハッシュベースルーティング
function useHashRouter() {
  const [route, setRoute] = useState(() => parseHash(window.location.hash));

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(parseHash(window.location.hash));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (path, params = {}) => {
    const query = Object.entries(params)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    window.location.hash = query ? `${path}?${query}` : path;
  };

  return { route, navigate };
}

function parseHash(hash) {
  const raw = (hash || '').replace(/^#\/?/, '');
  const [path, queryStr] = raw.split('?');
  const params = {};
  if (queryStr) {
    queryStr.split('&').forEach(pair => {
      const [k, v] = pair.split('=');
      if (k) params[k] = decodeURIComponent(v || '');
    });
  }
  return { path: path || 'home', params };
}

function App() {
  const { route, navigate } = useHashRouter();
  const [playerId, setPlayerId] = useState(null);
  const [nickname, setNickname] = useState(() => getSavedNickname() || '');
  const [loading, setLoading] = useState(true);

  // Firebase認証初期化 + Firestore問題プリロード
  useEffect(() => {
    async function initialize() {
      // 認証とFirestore問題を並行して初期化
      const [user] = await Promise.all([
        ensureAuth(),
        initQuestionsFromFirestore(),
      ]);

      if (user) {
        setPlayerId(user.uid);
      }
      setLoading(false);
    }
    initialize();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="animate-pulse" style={{ fontSize: '48px', marginBottom: '16px' }}>
            🎯
          </div>
          <p className="text-muted">読み込み中...</p>
        </div>
      </div>
    );
  }

  // ルーティング
  const { path, params } = route;

  const screenProps = {
    navigate,
    playerId,
    nickname,
    setNickname,
    params,
  };

  const renderScreen = () => {
    switch (path) {
      case 'wait':
        return <Wait {...screenProps} roomId={params.roomId} />;
      case 'game':
        return <Game {...screenProps} roomId={params.roomId} />;
      case 'result':
        return <Result {...screenProps} roomId={params.roomId} />;
      case 'tv':
        // TV専用表示モード（入力なし、16:9フルスクリーン）
        return <TVDisplay roomId={params.roomId} />;
      // チェーンモード
      case 'chain-order':
        return <ChainOrder {...screenProps} roomId={params.roomId} />;
      case 'chain-game':
        return <ChainGame {...screenProps} roomId={params.roomId} />;
      case 'chain-tv':
        return <ChainTV roomId={params.roomId} />;
      case 'chain-result':
        return <ChainResult {...screenProps} roomId={params.roomId} />;
      case 'admin':
        return <Admin {...screenProps} />;
      // 法的ページ
      case 'privacy':
        return <Privacy {...screenProps} />;
      case 'terms':
        return <Terms {...screenProps} />;
      case 'legal':
        return <Legal {...screenProps} />;
      default:
        return <Home {...screenProps} />;
    }
  };

  return (
    <ErrorBoundary>
      {renderScreen()}
    </ErrorBoundary>
  );
}

export default App;
